import type { ParticipantRole, PresentationStatus } from '../store/registrationsSlice'
import { getApiToken } from '@proaxiv/server-api'
import { forceFileDownload } from './forceFileDownload'

// ── Role mapping ──────────────────────────────────────────────────────────────

const API_TO_UI_ROLE: Record<string, ParticipantRole> = {
  AUDIENCE: 'Audience',
  SPEAKER: 'Speaker',
  STAFF: 'Staff',
  HOST: 'Host',
}

const UI_TO_API_ROLE: Record<ParticipantRole, string> = {
  Audience: 'AUDIENCE',
  Speaker: 'SPEAKER',
  Staff: 'STAFF',
  Host: 'HOST',
}

/** Maps a backend role string to the UI ParticipantRole enum value. */
export function apiRoleToUiRole(role: string | undefined | null): ParticipantRole {
  return API_TO_UI_ROLE[role ?? ''] ?? 'Audience'
}

/** Maps a UI ParticipantRole enum value to the backend role string. */
export function uiRoleToApiRole(role: ParticipantRole): string {
  return UI_TO_API_ROLE[role]
}

// ── Media consent ─────────────────────────────────────────────────────────────

/**
 * Whether the participant should be shown as having granted media consent.
 *
 * Organiser backup-registration treats consent as implied (the organiser confirms
 * it by submitting the form), so a stored `false` still displays as Yes when
 * `registeredByOrganizer` is true. That covers existing records written before
 * the write-path started persisting `true`.
 */
export function hasGrantedMediaConsent(participant: {
  consentMediaRecording?: boolean | null
  registeredByOrganizer?: boolean | null
}): boolean {
  return Boolean(participant.consentMediaRecording) || Boolean(participant.registeredByOrganizer)
}

// ── Name formatting ───────────────────────────────────────────────────────────

/** Formats a person's display name as "Last First" (last name leads), skipping missing parts. */
export function formatFullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  return [lastName, firstName]
    .map((p) => p?.trim())
    .filter((p): p is string => !!p)
    .join(' ')
}

// ── Presentation status mapping ───────────────────────────────────────────────

const API_TO_UI_PRES_STATUS: Record<string, PresentationStatus> = {
  NONE: 'none',
  REMINDER_SENT: 'reminder_sent',
  UPLOADED: 'uploaded',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
}

/** Maps a backend presentation status string to the UI PresentationStatus enum value. */
export function apiPresStatusToUiStatus(status: string | undefined | null): PresentationStatus {
  return API_TO_UI_PRES_STATUS[status ?? ''] ?? 'none'
}

// ── Presentation download ─────────────────────────────────────────────────────

interface DownloadPresResponse {
  presignedUrl?: string
  fileName?: string
}

/** Fetches a presigned download URL from the API and forces a file download (never opens inline). */
export async function apiDownloadPresentation(participantId: string): Promise<void> {
  const token = getApiToken()
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
  const res = await fetch(`/api/v1/events/participants/${participantId}/presentation/download`, {
    headers,
  })
  if (!res.ok) {
    throw new Error(`Download unavailable (${res.status})`)
  }
  const data = (await res.json()) as DownloadPresResponse
  if (!data.presignedUrl) {
    throw new Error('Download unavailable')
  }
  await forceFileDownload(data.presignedUrl, data.fileName)
}

// ── Ticket link ────────────────────────────────────────────────────────────────

interface TicketLinkResponse {
  ticketUrl?: string
}

/**
 * Turns an absolute or relative ticket URL from the API into an in-app path, appending the
 * management-page `from` param so "Back to event" returns to the tab the organiser was on.
 */
export function toTicketPathWithFrom(ticketUrl: string, from: string): string {
  try {
    const parsed = new URL(ticketUrl, 'http://local.invalid')
    const sep = parsed.search ? '&' : '?'
    return `${parsed.pathname}${parsed.search}${sep}from=${from}`
  } catch {
    return ticketUrl
  }
}

/**
 * Fetches a participant's real personal ticket link (the same magic link sent to them by
 * email) from the API. The link can't be constructed client-side — the `tk` token is
 * cryptographically derived server-side — so organizers must resolve it on demand rather than
 * building the URL from the participant/registration ID.
 */
export async function apiGetParticipantTicketLink(participantId: string): Promise<string | null> {
  const token = getApiToken()
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
  const res = await fetch(`/api/v1/events/participants/${participantId}/ticket-link`, { headers })
  if (!res.ok) return null
  const data = (await res.json()) as TicketLinkResponse
  return data.ticketUrl ?? null
}

/**
 * Resolves a participant's real ticket link and opens it in a new browser tab, always — the
 * organizer never navigates away from their current admin view to look at a participant's
 * ticket. Used by every "view ticket" action across the participant tables.
 */
export async function openParticipantTicketInNewTab(participantId: string): Promise<void> {
  const url = await apiGetParticipantTicketLink(participantId)
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}
